/**
 * MermaidRenderer.js
 * Component for rendering and editing Mermaid diagrams
 */

import React, { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { FaDownload, FaCode, FaExpand, FaCompress } from "react-icons/fa";

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: "basis",
  },
});

export default function MermaidRenderer({
  code,
  editable = false,
  onChange,
  onSave,
  title = "Diagram",
}) {
  const containerRef = useRef(null);
  const [editedCode, setEditedCode] = useState(code);
  const [error, setError] = useState(null);
  const [showCode, setShowCode] = useState(editable); // Show editor by default if editable
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [subgraphs, setSubgraphs] = useState([]);
  const [fullscreenSubgraph, setFullscreenSubgraph] = useState(null);
  const [diagramOrientation, setDiagramOrientation] = useState("LR"); // LR or TD

  useEffect(() => {
    setEditedCode(code);
  }, [code]);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Check if code has subgraphs
      const extractedSubgraphs = extractSubgraphs(editedCode);
      setSubgraphs(extractedSubgraphs);
      
      if (extractedSubgraphs.length > 0) {
        // Render each subgraph separately
        renderMultipleSubgraphs(extractedSubgraphs);
      } else {
        // Render as single diagram
        renderDiagram();
      }
    }, 300); // Debounce rendering

    return () => clearTimeout(timer);
  }, [editedCode, diagramOrientation]); // Re-render when code or orientation changes

  // Extract subgraphs from Mermaid code
  const extractSubgraphs = (code) => {
    const lines = code.split("\n");
    const subgraphs = [];
    let currentSubgraph = null;
    let subgraphContent = [];
    let braceCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Check if line starts a subgraph
      const subgraphMatch = line.match(/subgraph\s+(\w+)\s+\["([^"]+)"\]/);
      if (subgraphMatch) {
        currentSubgraph = {
          id: subgraphMatch[1],
          title: subgraphMatch[2],
          content: [],
        };
        subgraphContent = [];
        continue;
      }

      // Check if line ends subgraph
      if (line === "end" && currentSubgraph) {
        currentSubgraph.content = subgraphContent.join("\n").trim();
        subgraphs.push(currentSubgraph);
        currentSubgraph = null;
        subgraphContent = [];
        continue;
      }

      // Collect content inside subgraph
      if (currentSubgraph && line) {
        subgraphContent.push(lines[i]); // Keep original indentation
      }
    }

    console.log(
      `Extracted ${subgraphs.length} subgraphs:`,
      subgraphs.map((s) => s.title)
    );
    return subgraphs;
  };

  // Render multiple subgraphs as separate diagrams
  const renderMultipleSubgraphs = async (subgraphsList) => {
    if (!containerRef.current) return;

    try {
      setError(null);
      containerRef.current.innerHTML = "";

      const container = document.createElement("div");
      container.className = "multi-subgraph-container";

      for (let i = 0; i < subgraphsList.length; i++) {
        const sg = subgraphsList[i];

        // Create wrapper for each subgraph
        const wrapper = document.createElement("div");
        wrapper.className = "subgraph-wrapper";
        wrapper.dataset.subgraphId = sg.id;

        // Add title
        const titleEl = document.createElement("div");
        titleEl.className = "subgraph-title";
        titleEl.innerHTML = `<h3>${sg.title}</h3>`;

        // Add fullscreen button
        const fullscreenBtn = document.createElement("button");
        fullscreenBtn.className = "subgraph-fullscreen-btn";
        fullscreenBtn.innerHTML = "⛶ View Fullscreen";
        fullscreenBtn.onclick = () => setFullscreenSubgraph(i);
        titleEl.appendChild(fullscreenBtn);

        wrapper.appendChild(titleEl);

        // Create diagram container
        const diagramContainer = document.createElement("div");
        diagramContainer.className = "subgraph-diagram";

        // Render this subgraph as standalone flowchart (use current orientation)
        const subgraphCode = `flowchart ${diagramOrientation}\n${sg.content}`;
        const id = `mermaid-sg-${i}-${Date.now()}`;

        try {
          const { svg } = await mermaid.render(id, subgraphCode);
          diagramContainer.innerHTML = svg;
        } catch (err) {
          diagramContainer.innerHTML = `<div class="subgraph-error">Error rendering ${sg.title}: ${err.message}</div>`;
        }

        wrapper.appendChild(diagramContainer);
        container.appendChild(wrapper);
      }

      containerRef.current.appendChild(container);
    } catch (err) {
      console.error("Multi-subgraph render error:", err);
      setError(err.message || "Failed to render diagrams");
    }
  };

  const renderDiagram = async () => {
    if (!containerRef.current || !editedCode) return;

    try {
      setError(null);
      containerRef.current.innerHTML = "";

      const id = `mermaid-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`;
      const { svg } = await mermaid.render(id, editedCode);

      containerRef.current.innerHTML = svg;
    } catch (err) {
      console.error("Mermaid render error:", err);
      setError(err.message || "Failed to render diagram");
    }
  };

  const handleCodeChange = (newCode) => {
    setEditedCode(newCode);
    if (onChange) onChange(newCode);
  };

  const handleSave = () => {
    if (onSave) onSave(editedCode);
  };

  const downloadSVG = () => {
    const svg = containerRef.current.querySelector("svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title
      .replace(/\s+/g, "-")
      .toLowerCase()}-${Date.now()}.svg`;
    a.click();

    URL.revokeObjectURL(url);
  };

  const downloadPNG = async () => {
    const svg = containerRef.current.querySelector("svg");
    if (!svg) return;

    try {
      const canvas = document.createElement("canvas");
      const bbox = svg.getBBox();
      const scale = 2; // For better quality

      canvas.width = bbox.width * scale;
      canvas.height = bbox.height * scale;

      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        canvas.toBlob((blob) => {
          const pngUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = pngUrl;
          a.download = `${title
            .replace(/\s+/g, "-")
            .toLowerCase()}-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(pngUrl);
        });
      };

      img.src = url;
    } catch (err) {
      console.error("PNG export error:", err);
      alert("Failed to export PNG");
    }
  };

  const downloadMermaid = () => {
    const blob = new Blob([editedCode], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title
      .replace(/\s+/g, "-")
      .toLowerCase()}-${Date.now()}.mmd`;
    a.click();

    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(editedCode);
      alert("Mermaid code copied to clipboard!");
    } catch (err) {
      console.error("Copy error:", err);
      alert("Failed to copy");
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Render a single subgraph in fullscreen with zoom controls
  const renderSingleSubgraph = async (subgraph, container) => {
    if (!container) return;

    try {
      container.innerHTML = "";
      const subgraphCode = `flowchart ${diagramOrientation}\n${subgraph.content}`;
      const id = `mermaid-fullscreen-${Date.now()}`;
      const { svg } = await mermaid.render(id, subgraphCode);

      // Create zoom container
      const zoomContainer = document.createElement("div");
      zoomContainer.className = "zoom-container";
      zoomContainer.innerHTML = svg;

      // Add zoom and pan controls
      let scale = 1;
      let panX = 0;
      let panY = 0;
      let isPanning = false;
      let startX = 0;
      let startY = 0;

      const updateTransform = () => {
        zoomContainer.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
      };

      const zoomIn = () => {
        scale = Math.min(scale + 0.1, 3);
        updateTransform();
      };
      
      const zoomOut = () => {
        scale = Math.max(scale - 0.1, 0.3);
        updateTransform();
      };
      
      const resetZoom = () => {
        scale = 1;
        panX = 0;
        panY = 0;
        updateTransform();
      };
      
      // Add mouse wheel zoom
      container.addEventListener("wheel", (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
          zoomIn();
        } else {
          zoomOut();
        }
      });

      // Add drag to pan
      container.addEventListener("mousedown", (e) => {
        if (e.target === container || e.target.closest(".zoom-container")) {
          isPanning = true;
          startX = e.clientX - panX;
          startY = e.clientY - panY;
          container.style.cursor = "grabbing";
          e.preventDefault();
        }
      });

      container.addEventListener("mousemove", (e) => {
        if (isPanning) {
          panX = e.clientX - startX;
          panY = e.clientY - startY;
          updateTransform();
        }
      });

      container.addEventListener("mouseup", () => {
        if (isPanning) {
          isPanning = false;
          container.style.cursor = "grab";
        }
      });

      container.addEventListener("mouseleave", () => {
        if (isPanning) {
          isPanning = false;
          container.style.cursor = "grab";
        }
      });

      container.style.cursor = "grab";
      
      container.appendChild(zoomContainer);
      
      // Store zoom functions for external controls
      container.zoomIn = zoomIn;
      container.zoomOut = zoomOut;
      container.resetZoom = resetZoom;
    } catch (err) {
      container.innerHTML = `<div class="error-message"><p>Error: ${err.message}</p></div>`;
    }
  };

  return (
    <>
      {/* Fullscreen Modal for Individual Subgraph */}
      {fullscreenSubgraph !== null && subgraphs[fullscreenSubgraph] && (
        <div className="subgraph-fullscreen-modal">
          <div className="fullscreen-modal-header">
            <div className="header-left">
              <h2>{subgraphs[fullscreenSubgraph].title}</h2>
              <span className="subgraph-index">
                Process {fullscreenSubgraph + 1} of {subgraphs.length}
              </span>
            </div>
            <div className="header-right">
              <div className="layout-toggle fullscreen-layout-toggle">
                <button
                  className={`layout-btn ${
                    diagramOrientation === "LR" ? "active" : ""
                  }`}
                  onClick={() => setDiagramOrientation("LR")}
                  title="Horizontal layout"
                >
                  ↔
                </button>
                <button
                  className={`layout-btn ${
                    diagramOrientation === "TD" ? "active" : ""
                  }`}
                  onClick={() => setDiagramOrientation("TD")}
                  title="Vertical layout"
                >
                  ↕
                </button>
              </div>
              <div className="zoom-controls">
                <button
                  onClick={() => {
                    const container = document.querySelector(
                      ".fullscreen-diagram"
                    );
                    if (container && container.zoomOut) container.zoomOut();
                  }}
                  className="btn btn-icon zoom-btn"
                  title="Zoom out"
                >
                  −
                </button>
                <button
                  onClick={() => {
                    const container = document.querySelector(
                      ".fullscreen-diagram"
                    );
                    if (container && container.resetZoom) container.resetZoom();
                  }}
                  className="btn btn-icon zoom-btn"
                  title="Reset zoom"
                >
                  100%
                </button>
                <button
                  onClick={() => {
                    const container = document.querySelector(
                      ".fullscreen-diagram"
                    );
                    if (container && container.zoomIn) container.zoomIn();
                  }}
                  className="btn btn-icon zoom-btn"
                  title="Zoom in"
                >
                  +
                </button>
              </div>
              <button
                onClick={() => setFullscreenSubgraph(null)}
                className="btn btn-secondary"
              >
                ✕ Close
              </button>
            </div>
          </div>
          <div className="fullscreen-hint">
            💡 <strong>Zoom:</strong> Mouse wheel or +/− buttons • <strong>Pan:</strong> Click and drag to move • <strong>Layout:</strong> ↔/↕ buttons to switch orientation • <strong>Reset:</strong> 100% button
          </div>
          <div className="modal-diagram-container">
            <div
              ref={(el) => {
                if (el) {
                  renderSingleSubgraph(subgraphs[fullscreenSubgraph], el);
                }
              }}
              className="fullscreen-diagram"
            />
          </div>
        </div>
      )}

      <div className={`mermaid-renderer ${isFullscreen ? "fullscreen" : ""}`}>
        {/* Toolbar */}
        <div className="renderer-toolbar">
          <div className="toolbar-left">
            <h3>{title}</h3>
          </div>
          <div className="toolbar-right">
            {/* Layout Toggle */}
            {subgraphs.length > 0 && (
              <div className="layout-toggle">
                <button
                  className={`layout-btn ${
                    diagramOrientation === "LR" ? "active" : ""
                  }`}
                  onClick={() => setDiagramOrientation("LR")}
                  title="Horizontal layout (timeline)"
                >
                  ↔ Horizontal
                </button>
                <button
                  className={`layout-btn ${
                    diagramOrientation === "TD" ? "active" : ""
                  }`}
                  onClick={() => setDiagramOrientation("TD")}
                  title="Vertical layout (top to bottom)"
                >
                  ↕ Vertical
                </button>
              </div>
            )}
            {editable && (
              <button
                onClick={() => setShowCode(!showCode)}
                className="btn btn-icon"
                title="Toggle split-panel editor"
              >
                <FaCode />
                {showCode ? " Hide Editor" : " Show Editor"}
              </button>
            )}
            <button
              onClick={toggleFullscreen}
              className="btn btn-icon"
              title="Toggle fullscreen"
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
            <div className="download-group">
              <button
                onClick={downloadSVG}
                className="btn btn-icon"
                title="Download as SVG"
              >
                <FaDownload /> SVG
              </button>
              <button
                onClick={downloadPNG}
                className="btn btn-icon"
                title="Download as PNG"
              >
                <FaDownload /> PNG
              </button>
              <button
                onClick={downloadMermaid}
                className="btn btn-icon"
                title="Download Mermaid source"
              >
                <FaDownload /> Mermaid
              </button>
            </div>
            <button onClick={copyToClipboard} className="btn btn-secondary">
              Copy Code
            </button>
            {editable && onSave && (
              <button onClick={handleSave} className="btn btn-primary">
                Save Changes
              </button>
            )}
          </div>
        </div>

        {/* Split Panel: Code Editor (left) + Preview (right) */}
        {editable && showCode ? (
          <div className="split-panel">
            {/* Code Editor - Left Panel */}
            <div className="editor-panel">
              <div className="panel-header">
                <h4>Mermaid Code</h4>
                <div className="panel-header-actions">
                  <button
                    onClick={() => setShowHelp(!showHelp)}
                    className="help-button"
                    title="Show syntax help"
                  >
                    {showHelp ? "Hide Help" : "? Syntax Help"}
                  </button>
                  <span className="char-count">
                    {editedCode.length} characters
                  </span>
                </div>
              </div>
              {showHelp && (
                <div className="syntax-help">
                  <div className="help-section">
                    <strong>Quick Reference:</strong>
                    <code>A[Label] --> B[Next]</code> - Simple edge
                    <br />
                    <code>A -->|Text| B</code> - Labeled edge
                    <br />
                    <code>A{"{Question?}"}</code> - Decision node
                    <br />
                    <code>subgraph id ["Name"]</code> - Start subgraph
                    <br />
                    <code>end</code> - Close subgraph
                  </div>
                </div>
              )}
              <textarea
                value={editedCode}
                onChange={(e) => handleCodeChange(e.target.value)}
                className="mermaid-code-textarea"
                spellCheck={false}
                placeholder="Enter Mermaid syntax here..."
              />
            </div>

            {/* Preview - Right Panel */}
            <div className="preview-panel">
              <div className="panel-header">
                <h4>Live Preview</h4>
                {error && <span className="error-badge">Syntax Error</span>}
                {subgraphs.length > 0 && (
                  <span className="info-badge">
                    {subgraphs.length} Processes
                  </span>
                )}
              </div>
              <div className="diagram-preview preview-panel-scroll">
                {error ? (
                  <div className="error-message">
                    <h4>Failed to render diagram</h4>
                    <p>{error}</p>
                    <p className="error-hint">
                      Fix the syntax in the left panel. Common issues:
                      <br />- Incomplete arrows (use -->)
                      <br />- Missing node definitions
                      <br />- Invalid characters in node IDs
                      <br />- Don't use "direction TD" inside subgraphs
                    </p>
                  </div>
                ) : (
                  <div ref={containerRef} className="mermaid-container" />
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Single Panel Preview (when editor is hidden or not editable) */
          <div className="diagram-preview">
            {error ? (
              <div className="error-message">
                <h4>Failed to render diagram</h4>
                <p>{error}</p>
              </div>
            ) : (
              <div ref={containerRef} className="mermaid-container" />
            )}
          </div>
        )}

        {/* Code Display (read-only, for non-editable mode) */}
        {!editable && showCode && (
          <div className="code-display">
            <div className="code-display-header">
              <h4>Mermaid Source Code</h4>
            </div>
            <pre>
              <code>{editedCode}</code>
            </pre>
          </div>
        )}
      </div>
    </>
  );
}
